import { useState, useRef } from 'react';
import { X, Download, FileSpreadsheet, AlertCircle, CheckCircle, Loader2, Upload, RefreshCw } from 'lucide-react';
import { GameHistoryItem } from '../modules/stats/types';
import { DojosRow, SeasonsRow } from '../types/database';
import {
  exportToExcel,
  exportToCSV,
} from '../utils/exportImport';
import { parseExcelFile } from '../utils/excelImport';

interface ExportImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  games: GameHistoryItem[];
  dojos: DojosRow[];
  seasons: SeasonsRow[];
  onImportGames?: (games: Partial<GameHistoryItem>[]) => Promise<void>;
}

export default function ExportImportModal({
  isOpen,
  onClose,
  games,
  dojos,
  seasons,
  onImportGames,
}: ExportImportModalProps) {
  const [activeTab, setActiveTab] = useState<'export' | 'import'>('export');
  const [exportFormat, setExportFormat] = useState<'excel' | 'csv'>('excel');
  
  const [importProgress, setImportProgress] = useState<{ phase: string; message: string } | null>(null);
  const [importResult, setImportResult] = useState<{
    success: boolean;
    importedCount: number;
    errorCount: number;
    duplicateCount: number;
    errors: { row: number; errors: string[] }[];
    duplicates: { row: number; reason: string }[];
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleExport = () => {
    if (exportFormat === 'excel') {
      exportToExcel(games, dojos, seasons);
    } else {
      exportToCSV(games, dojos, seasons);
    }
  };

  const handleFileSelect = async (file: File) => {
    setImportProgress({ phase: 'parsing', message: '正在解析 Excel 文件...' });
    setImportResult(null);

    try {
      const result = await parseExcelFile(file, games);
      
      setImportProgress({ phase: 'importing', message: `解析完成，正在导入 ${result.games.length} 条游戏记录...` });

      if (result.games.length > 0 && onImportGames) {
        await onImportGames(result.games);
      }

      setImportResult({
        success: true,
        importedCount: result.games.length,
        errorCount: result.errors.length,
        duplicateCount: result.duplicates.length,
        errors: result.errors,
        duplicates: result.duplicates,
      });
      
      setImportProgress({ phase: 'complete', message: '导入完成' });
    } catch (error) {
      setImportResult({
        success: false,
        importedCount: 0,
        errorCount: 1,
        duplicateCount: 0,
        errors: [{ row: 0, errors: [error instanceof Error ? error.message : '未知错误'] }],
        duplicates: [],
      });
      setImportProgress({ phase: 'error', message: '导入失败' });
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-avalon-bg-dark rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-avalon-purple/20">
          <h2 className="text-lg font-bold text-white">数据导入导出</h2>
          <button
            onClick={() => {
              onClose();
            }}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-avalon-text hover:text-white hover:bg-avalon-card transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex border-b border-avalon-purple/20">
          <button
            onClick={() => setActiveTab('export')}
            className={`flex-1 py-3 px-4 flex items-center justify-center gap-2 transition-colors ${
              activeTab === 'export'
                ? 'bg-avalon-purple text-white'
                : 'text-avalon-text hover:text-white hover:bg-avalon-card'
            }`}
          >
            <Download className="w-4 h-4" />
            <span>导出数据</span>
          </button>
          <button
            onClick={() => setActiveTab('import')}
            className={`flex-1 py-3 px-4 flex items-center justify-center gap-2 transition-colors ${
              activeTab === 'import'
                ? 'bg-green-500 text-white'
                : 'text-avalon-text hover:text-white hover:bg-avalon-card'
            }`}
          >
            <Upload className="w-4 h-4" />
            <span>导入数据</span>
          </button>
        </div>

        <div className="p-4 max-h-[60vh] overflow-y-auto">
          {activeTab === 'export' && (
            <div className="space-y-4">
              <div className="bg-avalon-card/50 rounded-lg p-4">
                <h3 className="text-white font-medium mb-2 flex items-center gap-2">
                  <FileSpreadsheet className="w-4 h-4 text-avalon-gold" />
                  导出字段说明
                </h3>
                <p className="text-avalon-text text-sm mb-2">
                  导出固定字段：对局 ID、日期、道场、赛季、玩家昵称、角色、胜利方式、任务进程、MVP、积分变化
                </p>
                <p className="text-avalon-text/70 text-xs">
                  文件名格式：Avalon_战绩_YYYYMMDD_HHMMSS.xlsx
                </p>
              </div>

              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="excel"
                    checked={exportFormat === 'excel'}
                    onChange={() => setExportFormat('excel')}
                    className="w-4 h-4 accent-avalon-purple"
                  />
                  <span className="text-white text-sm">Excel (.xlsx)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="csv"
                    checked={exportFormat === 'csv'}
                    onChange={() => setExportFormat('csv')}
                    className="w-4 h-4 accent-avalon-purple"
                  />
                  <span className="text-white text-sm">CSV (.csv)</span>
                </label>
              </div>

              <button
                onClick={handleExport}
                className="w-full py-3 px-4 bg-avalon-purple text-white rounded-lg hover:bg-avalon-purple/80 transition-colors flex items-center justify-center gap-2"
              >
                <Download className="w-4 h-4" />
                导出 {games.length} 条记录
              </button>
            </div>
          )}

          {activeTab === 'import' && (
            <div className="space-y-4">
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                <h4 className="text-green-400 font-medium mb-2 flex items-center gap-2">
                  <Upload className="w-4 h-4" />
                  使用说明
                </h4>
                <ol className="text-avalon-text text-sm space-y-1 list-decimal list-inside">
                  <li>在模板中填写或粘贴战绩数据（第 11 行开始）</li>
                  <li>确保第 10 行为表头，不要修改表头字段</li>
                  <li>将填写好的文件拖拽到下方区域或点击选择文件</li>
                  <li>系统将自动解析并导入数据</li>
                </ol>
                <p className="text-avalon-text/70 text-xs mt-2">
                  支持字段：战绩更新日期、对局日期、当日局数、本局游戏人数、道场、赛季、玩家昵称、扮演角色、胜利方式、游戏进程、是否 MVP
                </p>
              </div>

              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-avalon-purple/40 rounded-lg p-8 text-center cursor-pointer hover:border-green-500/60 hover:bg-green-500/5 transition-all"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileInputChange}
                  className="hidden"
                />
                <Upload className="w-12 h-12 text-avalon-purple mx-auto mb-3" />
                <p className="text-white font-medium mb-1">
                  点击选择文件或拖拽 Excel 文件到此处
                </p>
                <p className="text-avalon-text text-xs">
                  支持格式：.xlsx, .xls
                </p>
              </div>

              {importProgress && (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      importProgress.phase === 'error' ? 'bg-red-500/20 text-red-400' :
                      importProgress.phase === 'complete' ? 'bg-green-500/20 text-green-400' :
                      'bg-blue-500/20 text-blue-400'
                    }`}>
                      {importProgress.phase === 'error' ? <AlertCircle className="w-5 h-5" /> :
                       importProgress.phase === 'complete' ? <CheckCircle className="w-5 h-5" /> :
                       <Loader2 className="w-5 h-5 animate-spin" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-medium">
                        {importProgress.phase === 'parsing' && '解析文件中'}
                        {importProgress.phase === 'importing' && '导入数据中'}
                        {importProgress.phase === 'complete' && '导入完成'}
                        {importProgress.phase === 'error' && '导入失败'}
                      </p>
                      <p className="text-avalon-text text-sm">{importProgress.message}</p>
                    </div>
                  </div>
                </div>
              )}

              {importResult && (
                <div className={`rounded-lg p-4 ${
                  importResult.success ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'
                }`}>
                  <div className="flex items-center gap-2 mb-3">
                    {importResult.success ? (
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-400" />
                    )}
                    <span className={`font-medium ${importResult.success ? 'text-green-400' : 'text-red-400'}`}>
                      {importResult.success ? '导入成功' : '导入失败'}
                    </span>
                  </div>

                  {importResult.success && (
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div className="bg-green-500/20 border border-green-500/30 rounded p-2 text-center">
                        <div className="text-green-400 font-bold">{importResult.importedCount}</div>
                        <div className="text-avalon-text text-xs">成功导入</div>
                      </div>
                      <div className="bg-avalon-card/50 rounded p-2 text-center">
                        <div className="text-red-400 font-bold">{importResult.errorCount}</div>
                        <div className="text-avalon-text text-xs">错误行数</div>
                      </div>
                      <div className="bg-yellow-500/20 border border-yellow-500/30 rounded p-2 text-center">
                        <div className="text-yellow-400 font-bold">{importResult.duplicateCount}</div>
                        <div className="text-avalon-text text-xs">重复跳过</div>
                      </div>
                    </div>
                  )}

                  {importResult.duplicates.length > 0 && (
                    <div className="mt-3">
                      <div className="text-yellow-400 font-medium text-sm mb-2 flex items-center gap-1">
                        <AlertCircle className="w-3.5 h-3.5" />
                        重复数据详情（已跳过）:
                      </div>
                      <div className="max-h-32 overflow-y-auto space-y-1">
                        {importResult.duplicates.slice(0, 5).map((dup, idx) => (
                          <div key={idx} className="text-yellow-400/80 text-xs bg-yellow-500/10 px-2 py-1 rounded">
                            第 {dup.row} 行：{dup.reason}
                          </div>
                        ))}
                        {importResult.duplicates.length > 5 && (
                          <div className="text-avalon-text text-xs">
                            还有 {importResult.duplicates.length - 5} 条重复...
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {importResult.errors.length > 0 && (
                    <div className="mt-3 max-h-32 overflow-y-auto">
                      {importResult.errors.slice(0, 5).map((error, idx) => (
                        <div key={idx} className="text-red-400 text-xs mb-1">
                          第 {error.row} 行：{error.errors.join(', ')}
                        </div>
                      ))}
                      {importResult.errors.length > 5 && (
                        <div className="text-avalon-text text-xs">
                          还有 {importResult.errors.length - 5} 条错误...
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="bg-avalon-card/30 rounded-lg p-3 text-sm">
                <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 text-avalon-gold" />
                  导入说明
                </h4>
                <ul className="text-avalon-text space-y-1 text-xs">
                  <li>• 从第 10 行读取表头，第 11 行开始读取数据</li>
                  <li>• 根据"对局日期 + 当日局数 + 人数 + 道场 + 赛季"自动去重</li>
                  <li>• 自动检查历史记录和表格内的重复数据</li>
                  <li>• 自动计算积分、阵营、胜负</li>
                  <li>• 同一游戏的多个玩家会自动合并</li>
                  <li>• 支持批量导入多局游戏数据</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
